import CloseIcon from '@mui/icons-material/Close';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import {Responsive, useContainerWidth, verticalCompactor} from 'react-grid-layout';
import type {Layout, LayoutItem} from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import {LiveProvider, LivePreview, LiveError} from 'react-live';
import * as MUI from '@mui/material';
import React, {useCallback, useContext, useMemo} from "react";
import {SpaceContext} from "../SpaceProvider.tsx";
import {resolveApiUrl} from "../resolveApiUrl.ts";
import {
  Auth,
  User,
  Storage,
  Database,
  Settings,
  Functions,
  Secrets,
  Policy,
  App,
  Analytics,
  Client
} from "@taruvi/sdk";

const {Box} = MUI;

const scope = {
  React,
  MUI,
  Auth,
  User,
  Storage,
  Database,
  Settings,
  Functions,
  Secrets,
  Policy,
  App,
  Analytics,
};

const circleBtn = {
  width: 28, height: 28, borderRadius: '50%', bgcolor: 'white', border: '1px solid #ddd',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  '&:hover': {bgcolor: '#f0f7ff', borderColor: '#1976d2'},
} as const;

/**
 * Wraps widget code in a function component so hooks work.
 * Widget authors write code as if inside a component body and call render() with JSX.
 * Material UI is exposed only through the MUI namespace, e.g. MUI.Box and MUI.Dialog.
 * We rewrite render() to set the return value of the wrapper component.
 */
const wrapWidgetCode = (code: string) => `const WidgetComponent = () => {${code.replace(/\brender\s*\(/, 'return (')}}; render(<WidgetComponent />);`;

export const GridContainer = () => {
  const {width, containerRef, mounted} = useContainerWidth();
  const [, spaceConfig, setSpaceConfig, , isEditing] = useContext(SpaceContext)!;
  const activeConfig = useMemo(() => spaceConfig.filter((w: any) => !w.is_removed), [spaceConfig]);
  const layouts = useMemo<LayoutItem[]>(() => activeConfig.map((item: any) => item.layouts).filter(Boolean), [activeConfig]);

  const taruviClients = useMemo<Record<string, Client>>(() => {
    const apiUrl = resolveApiUrl();

    return spaceConfig.reduce((acc: Record<string, Client>, item: any) => {
      acc[item.id] = new Client({
        apiKey: import.meta.env.VITE_API_KEY,
        apiUrl,
        appSlug: item.appSlug
      })
      return acc
    }, {})
  }, [spaceConfig]);

  const widgetScopes = useMemo(() => {
    const scopes: Record<string, typeof scope & {taruviClient: Client}> = {};
    spaceConfig.forEach((item: any) => {
      if (taruviClients[item.id]) scopes[item.id] = {...scope, taruviClient: taruviClients[item.id]};
    });
    return scopes;
  }, [taruviClients]);

  const handleRemoveWidget = useCallback((id: string) => {
    setSpaceConfig((prev: any) => prev.map((w: any) =>
      w.id === id && w.is_default ? {...w, is_removed: true} : w
    ).filter((w: any) => w.id !== id || w.is_default));
  }, []);

  const persistLayout = useCallback((newLayout: Layout) => {
    setSpaceConfig((prev: any) => prev.map((item: any) => {
      const updated = newLayout.find((l: LayoutItem) => l.i === item.id);
      return updated ? {...item, layouts: updated} : item;
    }));
  }, []);

  return (
    <div ref={containerRef}>
      {mounted && (
        <Responsive
          layouts={{lg: layouts}}
          breakpoints={{lg: 900, md: 600, sm: 0}}
          cols={{lg: 4, md: 3, sm: 2}}
          rowHeight={150}
          margin={[20, 20]}
          width={width}
          dragConfig={{enabled: isEditing, handle: '.edit-drag-handle'}}
          resizeConfig={{enabled: isEditing, handles: ['se']}}
          compactor={verticalCompactor}
          onDragStop={persistLayout}
          onResizeStop={persistLayout}
          className={isEditing ? 'editing' : 'no-edit'}
        >
          {activeConfig.map(({id, react_code_content, style_code_content, layouts: itemLayouts}: any) => (
            <div key={id} style={{position: 'relative'}}>
              <div id={`widget-${id}`} className="widget-card" style={{
                position: 'absolute',
                inset: 0,
                overflow: 'visible',
                borderRadius: 24,
                boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
                background: 'rgba(255,255,255,0.6)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(192,199,213,0.15)',
                transition: 'transform 0.2s ease, box-shadow 0.2s ease',
              }}>

                {isEditing && <>
                  {/* Position drag - top center */}
                    <div className="edit-drag-handle" style={{
                      position: 'absolute',
                      top: -14,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      zIndex: 10,
                      cursor: 'grab'
                    }}>
                        <Box sx={circleBtn}><DragIndicatorIcon sx={{fontSize: 16, color: '#666'}}/></Box>
                    </div>

                  {/* Close - top right */}
                    <Box onClick={() => handleRemoveWidget(id)}
                         sx={{
                           position: 'absolute',
                           top: -14,
                           right: -14,
                           zIndex: 10,
                           cursor: 'pointer',
                         }}>
                        <Box sx={{...circleBtn, '&:hover': {bgcolor: '#ffebee', borderColor: '#ef5350'}}}>
                          <CloseIcon sx={{fontSize: 16, color: '#ef5350'}}/>
                        </Box>
                    </Box>

                  {/* Size badge - bottom left */}
                    <Box sx={{
                      position: 'absolute',
                      bottom: -8,
                      left: -6,
                      zIndex: 10,
                      bgcolor: '#1976d2',
                      color: 'white',
                      border: '2px solid white',
                      borderRadius: '25px',
                      px: 1.2,
                      py: 0.4,
                      fontSize: 12,
                      fontWeight: 600
                    }}>
                      {itemLayouts?.w || '?'}×{itemLayouts?.h || '?'}
                    </Box>

                  {/* Resize indicator - bottom right */}
                    <Box sx={{position: 'absolute', bottom: -10, right: -10, zIndex: 10, pointerEvents: 'none'}}>
                        <Box sx={circleBtn}><OpenInFullIcon
                            sx={{fontSize: 14, color: '#666', transform: 'rotate(90deg)'}}/></Box>
                    </Box>
                </>}

                {style_code_content && (
                  <style>{style_code_content.replace(/(^|\})\s*([^@{}][^{]*)\{/g, `$1 #widget-${id} $2{`)}</style>
                )}
                <div className="widget-root" style={{height: '100%', overflow: 'auto'}}>
                  <LiveProvider code={wrapWidgetCode(react_code_content || '')} scope={widgetScopes[id]} noInline>
                    <LivePreview style={{height: '100%', width: '100%', minHeight: 0, overflow: 'auto'}}/>
                    <LiveError/>
                  </LiveProvider>
                </div>
              </div>
            </div>
          ))}
        </Responsive>
      )}
    </div>
  );
};
